// useRetryCounter.js - Hook compartido para lógica de reintentos y control de fallback
import { useState, useCallback } from 'react';

/**
 * Hook reutilizable para lógica de reintentos/fallback en multimedia (imagen, video, audio, etc.)
 * @param {Object} options
 * @param {number} options.maxAttempts - Número máximo de reintentos
 * @param {function} [options.onLastChance] - Callback cuando se activa el fallback visual
 * @returns {Object}
 *   - retryCounter: número de intento actual
 *   - lastChance: boolean si está en último intento/fallback
 *   - increment: función para aumentar el contador
 *   - setLastChance: función para forzar el fallback
 *   - reset: función para reiniciar el contador
 */
export default function useRetryCounter({ maxAttempts = 2, onLastChance } = {}) {
  const [retryCounter, setRetryCounter] = useState(0);
  const [lastChance, setLastChance] = useState(false);

  // Incrementa el contador y activa fallback si corresponde
  const increment = useCallback(() => {
    setRetryCounter(prev => {
      if (prev + 1 < maxAttempts) {
        return prev + 1;
      } else {
        setLastChance(lc => {
          if (!lc) {
            onLastChance?.();
            return true;
          }
          return lc;
        });
        return prev + 1;
      }
    });
  }, [maxAttempts, onLastChance]);

  // Reinicia el contador y el fallback
  const reset = useCallback(() => {
    setRetryCounter(0);
    setLastChance(false);
  }, []);

  return {
    retryCounter,
    lastChance,
    increment,
    setLastChance,
    reset
  };
}
